import CIMSCollapse from 'components/CIMSCollapse'
import { TestFormItemsProps, TestFormItemsValue } from './types'
import { Space } from 'antd'
import { useCollapseButton } from '../../hooks/useCollapseButton'
import { useBreakDataFormItemColumns } from '../../hooks/useBreakDataFormItemColumns'
import TabularFormItems from 'components/TabularFormItems'
import { useEffect, useState } from 'react'
import { alphabeticalCompare, alphabeticalSort } from 'shared'
import { useBreakDateByPlacementDate } from '../../../../hooks/useBreakDateByPlacementDate'
import { errorHandler } from 'hooks/errorHandler'
import { Moment } from 'moment'
import { LabeledValueType } from 'components/CIMSSelect/types'
import { useRemoveConcreteTestBreakDataMutation } from 'schema'
import { DefaultMutationHookOptions } from 'utils/defaults'
import { simpleSuccessNotification } from 'hooks/notificationHandler'
import { useConcreteTestSetTrackerContext } from '../../../../ConcreteTestSetTrackerContext'
import StrengthCount from '../StrengthCount'
import CIMSSection from 'components/CIMSSection'

const TestFormItems: React.FC<TestFormItemsProps> = ({
  onChange,
  value,
  concreteTests,
  placementDate,
}) => {
  const { relations } = useConcreteTestSetTrackerContext()
  const [remove] = useRemoveConcreteTestBreakDataMutation({
    ...DefaultMutationHookOptions,
    onCompleted: ({
      removeConcreteTestBreakData: {
        concreteTestBreakData: {
          concreteTest: { fieldNumber, globalId },
        },
      },
    }) =>
      simpleSuccessNotification(
        `Break Data successfully removed from ${fieldNumber ?? globalId}`
      ),
  })
  const { CollapseButton, ...rest } = useCollapseButton(concreteTests)
  function change<Key extends keyof TestFormItemsValue>(
    key: Key,
    { id, tempId, concreteTestId }: TestFormItemsValue,
    value: TestFormItemsValue[Key]
  ) {
    setValues((prev) => {
      const found = prev.find((prev) =>
        id ? prev.id === id : tempId ? prev.tempId === tempId : false
      )
      return [
        ...prev.filter((prev) =>
          id ? prev.id !== id : tempId ? prev.tempId !== tempId : true
        ),
        {
          ...found,
          concreteTestId,
          [key]: value,
        },
      ]
    })
  }
  const columns = useBreakDataFormItemColumns<TestFormItemsValue>({
    type: 'test',
    onChange: {
      age: (r, v) => change('age', r, v as number),
      breakDate: (r, v) => change('breakDate', r, v as Moment),
      diameter: (r, v) => change('diameter', r, v as string),
      labNumber: (r, v) => change('labNumber', r, v as string),
      labTech: (r, v) => change('labTech', r, v as LabeledValueType),
      peakLoad: (r, v) => change('peakLoad', r, v as number),
      strength: (r, v) => change('strength', r, v as number),
      testingLab: (r, v) => change('testingLab', r, v as LabeledValueType),
      type: (r, v) => change('type', r, v as string),
    },
  })
  const [values, setValues] = useState(value ?? [])
  useEffect(() => {
    onChange?.(values)
    // don't listen to onChange
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values])
  const breakDateByPlacementDate = useBreakDateByPlacementDate(placementDate)
  return (
    <Space size="small" direction="vertical" style={{ width: '100%' }}>
      <CIMSSection title="Cylinders">
        <CIMSCollapse
          {...rest}
          multiple
          panels={concreteTests
            .sort(alphabeticalSort('fieldNumber'))
            .map(({ fieldNumber, globalId, strengthAge }) => {
              const records = values
                .filter(({ concreteTestId }) => concreteTestId === globalId)
                .sort((a, b) => {
                  const A = a.id?.toString() ?? a.tempId
                  const B = b.id?.toString() ?? b.tempId
                  if (a.age === b.age) return alphabeticalCompare(A, B)
                  return alphabeticalCompare(a.age, b.age)
                })
              return {
                key: globalId,
                header: fieldNumber ?? globalId,
                extra: {
                  type: 'default',
                  children: (
                    <StrengthCount
                      count={
                        !strengthAge && !records.length
                          ? undefined
                          : records.filter(
                              ({ strength }) => typeof strength === 'number'
                            ).length
                      }
                      max={records.length}
                    />
                  ),
                },
                children: (
                  <TabularFormItems
                    recordName="Cylinder Break Data"
                    add={{
                      onClick: () => {
                        setValues((prev) => [
                          ...prev,
                          {
                            age: 28,
                            breakDate: breakDateByPlacementDate(),
                            concreteTestId: globalId,
                            tempId: crypto.randomUUID(),
                          },
                        ])
                      },
                    }}
                    remove={{
                      onClick: async ({ id, tempId, ...rest }) => {
                        if (typeof id === 'number') {
                          await remove({ variables: { id } })
                          relations.refetch()
                          setValues((prev) =>
                            prev.filter((value) =>
                              typeof value.id === 'number'
                                ? value.id !== id
                                : true
                            )
                          )
                        } else if (tempId)
                          setValues((prev) =>
                            prev.filter((value) =>
                              !value.tempId ? true : value.tempId !== tempId
                            )
                          )
                        else
                          errorHandler(
                            'Incorrectly configured cylinder break data!',
                            [JSON.stringify({ id, tempId, ...rest }, null, 2)]
                          )
                      },
                    }}
                    records={records}
                    rowKey={({ id, concreteTestId, tempId }) =>
                      `${id ?? tempId}_${concreteTestId}`
                    }
                    columns={columns}
                  />
                ),
              }
            })}
        />
        {CollapseButton}
      </CIMSSection>
    </Space>
  )
}

export default TestFormItems
